import { type FC, useContext, useRef, useState } from "react";

import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faUpload } from "@fortawesome/pro-duotone-svg-icons/faUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  type ActiveStorageAttachment,
  type DocumentFragment,
  DocumentStatusEnum,
  type StepStates,
} from "@app_schema";

import {
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
  PDF_EXTENSION,
  PDF_MIME_TYPE,
} from "@application/utilities/file";

import { Button } from "@styled/button";
import { type ButtonColor } from "@styled/button_style";
import { FileContext } from "@styled/file_context";
import { FilePill } from "@styled/file_pill";

// Download labels for medical summary files: a case's exports by the slot
// they're attached to (their names carry the claimant), a legacy run's files
// by their fixed names.
const SUMMARY_SLOT_LABELS: Record<string, string> = {
  summary_indexed_pdf: "Indexed",
  summary_docx: "Summary",
  summary_pdf: "Summary PDF",
};

export const SUMMARY_DOWNLOAD_LABELS: Record<string, string> = {
  "medical_summary_indexed.pdf": "Indexed",
  "medical_summary_indexed.docx": "Indexed",
  "medical_summary.docx": "Summary",
  "medical_summary.pdf": "Summary PDF",
};

export const getDownloadLabel = ({
  name,
  filename,
}: Pick<ActiveStorageAttachment, "name" | "filename">): string =>
  SUMMARY_SLOT_LABELS[name] ?? SUMMARY_DOWNLOAD_LABELS[filename] ?? filename;

type StatusPillColor = "emerald" | "indigo" | "slate" | "rose";

const STATUS_HINTS: Record<
  DocumentStatusEnum,
  { text: string; color: StatusPillColor }
> = {
  [DocumentStatusEnum.Processed]: { text: "Complete", color: "emerald" },
  [DocumentStatusEnum.Processing]: { text: "Processing", color: "indigo" },
  [DocumentStatusEnum.Queueing]: { text: "Queueing", color: "indigo" },
  [DocumentStatusEnum.Initialized]: { text: "Queued", color: "slate" },
  [DocumentStatusEnum.Failed]: { text: "Failed", color: "rose" },
};

export function filePillStatus(
  doc: Pick<DocumentFragment, "status">,
): { text: string; color: StatusPillColor } | undefined {
  return STATUS_HINTS[doc.status] ?? undefined;
}

// List-context consumers (StepPills, projects_list) only need the
// underlying step enum triple — the progress-stepper fields on StepStates are
// details-page-only.
export type BaseStepStates = Pick<
  StepStates,
  "process" | "review" | "finalize"
>;

// Shared types
export type RunFile = {
  id: string;
  filename: string;
  bytesize: number;
  type?: string | null;
  url: string;
};

// Accepted file types for uploads
const ACCEPTED_FILE_TYPES = [
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
  PDF_EXTENSION,
  PDF_MIME_TYPE,
].join(",");

// File queue render helper
// A file the server refused wasn't used, and sending it again won't change
// that; with no reason, the upload itself failed.
const failedText = (error?: string) => (error ? "Not used" : "Upload failed");

export const renderFileQueueItem = ({
  file,
  progress,
  failed,
  error,
  onDismiss,
}: {
  file: File;
  progress?: { loaded: number; total: number };
  failed?: boolean;
  error?: string;
  onDismiss?: () => void;
}) => (
  <FilePill
    name={file.name}
    type={file.type}
    size={file.size}
    status={failed ? { text: failedText(error), color: "rose" } : undefined}
    progress={failed ? undefined : progress}
    error={failed ? error : undefined}
    onDismiss={failed ? onDismiss : undefined}
  />
);

// Upload button component
export const UploadButton: FC<{
  variant?: "upload" | "add";
  accept?: string;
  label?: string;
  color?: ButtonColor;
}> = ({
  variant = "upload",
  accept = ACCEPTED_FILE_TYPES,
  label,
  color = "indigo",
}) => {
  const { onSelect } = useContext(FileContext);
  const input = useRef<HTMLInputElement>(null);
  const [key, setKey] = useState<number>(0);

  const icon = variant === "add" ? faPlus : faUpload;
  const text = label ?? (variant === "add" ? "Add More" : "Upload");

  return (
    // `relative` gives the sr-only (position:absolute) input a positioned
    // ancestor — without it the input anchors to <body> and extends the
    // document height, creating phantom outer scroll on tall pages.
    <span className="relative">
      <input
        key={key}
        type="file"
        multiple
        accept={accept}
        ref={input}
        onChange={(event) => {
          const files = event.target.files;
          if (files) onSelect([...files]);
          setKey((k) => k + 1);
        }}
        className="sr-only"
      />
      <Button
        type="button"
        color={color}
        size="sm"
        onClick={() => input.current?.click()}
      >
        <FontAwesomeIcon icon={icon} /> {text}
      </Button>
    </span>
  );
};
